// Section 22 turn flow: End season shows a summary of what changed this season before the
// season resolves (a non-modal region under the header; the setting can turn it off).
import { useEffect, useRef } from 'preact/hooks';
import type { DraftSummaryVM } from '../../viewModels';
import { jobLabel } from '../../viewModels';
import { uiText } from '../text/en';

interface Props {
  summary: DraftSummaryVM;
  onConfirm: () => void;
  onCancel: () => void;
}

export function EndConfirm({ summary, onConfirm, onCancel }: Props) {
  const confirm = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirm.current?.focus();
  }, []);
  const t = uiText.confirm;
  const counts = [
    summary.assignments.length > 0 && t.jobs(summary.assignments.length),
    summary.policies.length > 0 && t.policies(summary.policies.length),
    summary.builds.length > 0 && t.builds(summary.builds.length),
    summary.renames.length > 0 && t.renames(summary.renames.length),
    summary.rationPins > 0 && t.pins(summary.rationPins),
    summary.answers > 0 && t.answers(summary.answers),
    summary.toolOverrides + summary.triageOverrides > 0 &&
      t.overrides(summary.toolOverrides + summary.triageOverrides),
  ].filter((x): x is string => x !== false);
  const buildKind: Record<string, string> = {
    queue: t.buildQueue,
    cancel: t.buildCancel,
    pause: t.buildPause,
    move: t.buildMove,
  };
  return (
    <section
      class="confirm"
      role="region"
      aria-labelledby="confirm-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <h2 id="confirm-title" class="confirm__title">
        {t.title}
      </h2>
      {summary.total === 0 ? (
        <p>{t.nothing}</p>
      ) : (
        <>
          <p>
            {t.changed}: {counts.join(', ')}.
          </p>
          <ul class="confirm__list">
            {summary.assignments.map((a) => (
              <li key={a.personId}>
                {t.move(a.name, jobLabel(a.from).toLowerCase(), jobLabel(a.to).toLowerCase())}
              </li>
            ))}
            {summary.policies.map((p) => (
              <li key={p.id}>{p.label}</li>
            ))}
            {summary.builds.map((b, i) => (
              <li key={`b${i}`}>
                {b.label} ({buildKind[b.kind] ?? b.kind})
              </li>
            ))}
            {summary.renames.map((r) => (
              <li key={r.personId}>
                {r.from} → {r.to}
              </li>
            ))}
          </ul>
        </>
      )}
      <div class="confirm__actions">
        <button ref={confirm} type="button" class="button button--primary" onClick={onConfirm}>
          {t.confirm}
        </button>
        <button type="button" class="button" onClick={onCancel}>
          {t.cancel}
        </button>
      </div>
    </section>
  );
}
